<script>
/**
 * @component UrgencyProviders
 * @description Administración de prestadores de urgencias. Permite asignar médicos
 *              a especialidades de urgencia habilitadas, ver la tabla de asignaciones
 *              y desactivar prestadores. Solo accesible para admin/health-admin.
 * @example <UrgencyProviders />
 */
import urgencyProviderService from "@/services/urgency-provider";
import urgencyConfigService from "@/services/urgency-config";
import api from "@/services/api";
import Spinner from "@/components/ui/Spinner";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import { mapGetters } from "vuex";
import { formatAR } from "@/services/datetime";

export default {
  name: "UrgencyProviders",
  components: { Spinner, BaseSubNav },

  data() {
    return {
      isLoading: false,
      providers: [],
      urgencyConfigs: [],
      doctors: [],
      filterSpecialty: "",
      filterActive: true,
      isDialogVisible: false,
      form: {
        doctorId: "",
        medicalSpecialtyId: "",
      },
      formErrors: {},
      isSaving: false,
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),

    /**
     * @description Prestadores filtrados por especialidad y estado
     * @returns {Array}
     */
    filteredProviders() {
      let list = this.providers;

      if (this.filterSpecialty) {
        list = list.filter(
          (p) =>
            p.medicalSpecialtyId === this.filterSpecialty ||
            p.medicalSpecialty?._id === this.filterSpecialty
        );
      }

      list = list.filter((p) => {
        const active = p.isActive !== false;
        return this.filterActive ? active : !active;
      });

      return list;
    },

    /**
     * @description Configs de urgencia habilitadas para el selector del diálogo
     * @returns {Array}
     */
    enabledConfigs() {
      return this.urgencyConfigs.filter((c) => c.isEnabled !== false);
    },
  },

  created() {
    this.fetchData();
  },

  methods: {
    /**
     * @description Carga prestadores, especialidades y médicos en paralelo
     * @returns {Promise<void>}
     */
    async fetchData() {
      this.isLoading = true;
      try {
        const [providers, configs, doctorsRes] = await Promise.all([
          urgencyProviderService.getAll(),
          urgencyConfigService.getAll(),
          api.get("/doctors"),
        ]);
        this.providers = providers;
        this.urgencyConfigs = configs.docs || configs;
        this.doctors = doctorsRes.data || doctorsRes;
      } catch (e) {
        this.$message.error("Error al cargar datos");
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Recarga solo la lista de prestadores
     * @returns {Promise<void>}
     */
    async fetchProviders() {
      try {
        this.providers = await urgencyProviderService.getAll();
      } catch (e) {
        this.$message.error("Error al cargar prestadores");
      }
    },

    /**
     * @description Obtiene el nombre de un médico por su ID
     * @param {Object} provider - Prestador con datos del doctor
     * @returns {string}
     */
    getDoctorName(provider) {
      if (provider.doctor) {
        const d = provider.doctor;
        return `${d.firstName || ""} ${d.lastName || ""}`.trim() || "—";
      }
      return "—";
    },

    /**
     * @description Obtiene el nombre de la especialidad de un prestador
     * @param {Object} provider - Prestador con datos de especialidad
     * @returns {string}
     */
    getSpecialtyName(provider) {
      if (provider.medicalSpecialty) {
        return (
          provider.medicalSpecialty.name ||
          provider.medicalSpecialty.specialty ||
          "—"
        );
      }
      const config = this.urgencyConfigs.find(
        (c) => c.medicalSpecialty?._id === provider.medicalSpecialtyId
      );
      return config?.medicalSpecialty?.name || "—";
    },

    /**
     * @description Obtiene el nombre de quien asignó al prestador
     * @param {Object} provider - Prestador con datos del asignador
     * @returns {string}
     */
    getAssignedBy(provider) {
      if (provider.assignedBy) {
        const u = provider.assignedBy;
        return `${u.firstName || ""} ${u.lastName || ""}`.trim() || "—";
      }
      return "—";
    },

    /**
     * @description Formatea la fecha de asignación
     * @param {string} dateStr - Fecha ISO string
     * @returns {string}
     */
    /**
     * @description UC-REG-079: TZ del tenant.
     */
    formatDate(dateStr) {
      if (!dateStr) return "—";
      return formatAR(dateStr, "DD/MM/YYYY HH:mm");
    },

    /**
     * @description Abre el diálogo de asignación de médico
     */
    openDialog() {
      this.isDialogVisible = true;
      this.formErrors = {};
      this.form = { doctorId: "", medicalSpecialtyId: "" };
    },

    /**
     * @description Cierra el diálogo de asignación
     */
    closeDialog() {
      this.isDialogVisible = false;
      this.form = { doctorId: "", medicalSpecialtyId: "" };
      this.formErrors = {};
    },

    /**
     * @description Asigna un médico como prestador de urgencia
     * @returns {Promise<void>}
     */
    async assignProvider() {
      this.formErrors = {};
      if (!this.form.doctorId) {
        this.formErrors.doctorId = "Seleccione un médico";
      }
      if (!this.form.medicalSpecialtyId) {
        this.formErrors.medicalSpecialtyId = "Seleccione una especialidad";
      }
      if (Object.keys(this.formErrors).length) return;

      this.isSaving = true;
      try {
        await urgencyProviderService.assign(this.form);
        this.$message.success("Médico asignado como prestador de urgencia");
        this.closeDialog();
        this.fetchProviders();
      } catch (e) {
        this.$message.error("Error al asignar prestador");
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * @description Desactiva un prestador de urgencia con confirmación
     * @param {Object} provider - Prestador a desactivar
     * @returns {Promise<void>}
     */
    async deactivateProvider(provider) {
      const doctorName = this.getDoctorName(provider);
      try {
        await this.$confirm(
          `¿Desactivar al prestador "${doctorName}"?`,
          "Desactivar prestador",
          {
            confirmButtonText: "Desactivar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await urgencyProviderService.deactivate(provider._id);
        this.$message.success("Prestador desactivado");
        this.fetchProviders();
      } catch {
        // Cancelado
      }
    },
  },
};
</script>

<template lang="pug">
section.urgency-providers
  BaseSubNav(title="Prestadores de Urgencias")

  .urgency-providers__header
    .urgency-providers__filters
      el-select(
        v-model="filterSpecialty"
        placeholder="Todas las especialidades"
        clearable
        size="small"
      )
        el-option(
          v-for="config in urgencyConfigs"
          :key="config.medicalSpecialty._id"
          :label="config.medicalSpecialty.name"
          :value="config.medicalSpecialty._id"
        )
      el-switch(
        v-model="filterActive"
        active-text="Activos"
        inactive-text="Inactivos"
        active-color="#0d9d9d"
        inactive-color="#cbd5e1"
      )

    button.button-teal-new(@click="openDialog")
      micon(name="add_circle_outline")
      span Asignar médico

  .urgency-providers__body
    .box.box--with-subnav
      .box__content--stretch
        spinner(v-if="isLoading")
        table(v-else-if="filteredProviders.length")
          thead
            tr
              th Médico
              th Especialidad
              th Asignado por
              th Fecha asignación
              th Estado
              th Acciones
          tbody
            tr(v-for="provider in filteredProviders" :key="provider._id")
              td.td--doctor {{ getDoctorName(provider) }}
              td {{ getSpecialtyName(provider) }}
              td {{ getAssignedBy(provider) }}
              td {{ formatDate(provider.createdAt) }}
              td.td--status
                span.status-badge(:class="provider.isActive !== false ? 'status-badge--active' : 'status-badge--inactive'")
                  | {{ provider.isActive !== false ? 'Activo' : 'Inactivo' }}
              td.td--actions
                .actions-container(v-if="provider.isActive !== false")
                  button.button.button--action.button--action-warning(
                    @click="deactivateProvider(provider)"
                    title="Desactivar"
                  )
                    micon(name="highlight_off")

        .urgency-providers__empty(v-if="!isLoading && !filteredProviders.length")
          micon(name="people")
          p No hay prestadores de urgencia {{ filterActive ? 'activos' : 'inactivos' }}

  el-dialog(
    title="Asignar Médico a Urgencias"
    :visible.sync="isDialogVisible"
    width="520px"
  )
    el-form(label-position="top" size="small")
      el-form-item(label="Médico" required :error="formErrors.doctorId")
        el-select(
          v-model="form.doctorId"
          placeholder="Seleccionar médico"
          filterable
          style="width: 100%"
        )
          el-option(
            v-for="doc in doctors"
            :key="doc._id"
            :label="`${doc.firstName || ''} ${doc.lastName || ''}`.trim()"
            :value="doc._id"
          )

      el-form-item(label="Especialidad de urgencia" required :error="formErrors.medicalSpecialtyId")
        el-select(
          v-model="form.medicalSpecialtyId"
          placeholder="Seleccionar especialidad"
          style="width: 100%"
        )
          el-option(
            v-for="config in enabledConfigs"
            :key="config.medicalSpecialty._id"
            :label="config.medicalSpecialty.name"
            :value="config.medicalSpecialty._id"
          )

    template(#footer)
      el-button(@click="closeDialog") Cancelar
      el-button(type="primary" @click="assignProvider" :loading="isSaving") Asignar
</template>

<style lang="scss" scoped>
.urgency-providers {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 12px;

    .button-teal-new {
      display: inline-flex;
      align-items: center;
      gap: 6px;

      .micon {
        width: 16px;
        height: 16px;
      }
    }
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: 12px;

    .el-select {
      width: 220px;
    }
  }

  &__body {
    .box__content--stretch {
      overflow-x: auto;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 16px;
    color: $text-muted-new;

    .micon {
      width: 40px;
      height: 40px;
      fill: $text-muted-new;
      margin-bottom: 12px;
    }

    p {
      font-size: 14px;
      margin: 0;
    }
  }
}

.td--doctor {
  font-weight: 600;
  color: $navy;
}

.td--status {
  width: 100px;
}

.td--actions {
  width: 80px;
}

.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.6;

  &--active {
    background: rgba(13, 157, 157, 0.1);
    color: #0d9d9d;
  }

  &--inactive {
    background: rgba(148, 163, 184, 0.15);
    color: #64748b;
  }
}

.actions-container {
  display: flex;
  gap: 4px;
}

.button--action {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  .micon {
    width: 18px;
    height: 18px;
    fill: $text-secondary-new;
  }

  &:hover {
    background: #f1f5f9;
    .micon {
      fill: $navy;
    }
  }

  &-warning:hover {
    background: rgba(245, 158, 11, 0.08);
    .micon {
      fill: #f59e0b;
    }
  }
}
</style>
